import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectFluxAutomaticControlPanelMltOption,
  selectFluxAutomaticControlPanelSelectedRevision,
  selectFluxAutomaticControlPanelUnicenarioScenario,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { RowData } from 'components/Flux/Revision/Table';
import { displayValue } from 'components/Flux/GeneralTable';
import { NA_LABEL } from 'helper/flux/automatic';
import { useLazyGetUnicenarioDataQuery } from 'services/api/products/flux';

const EM_DASH = '\u2014';

export interface UnicenarioSectionRow {
  category: string;
  rows: RowData[];
}

export default function useUnicenarioData() {
  const unicenarioScenario = useSelector(selectFluxAutomaticControlPanelUnicenarioScenario);
  const review = useSelector(selectFluxAutomaticControlPanelSelectedRevision);
  const revision = review ?? '';
  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);

  const [trigger, { data: rawData, isFetching }] = useLazyGetUnicenarioDataQuery();

  useEffect(() => {
    if (unicenarioScenario && revision.length > 0) {
      trigger({
        cenario: unicenarioScenario,
        revision,
        unidade,
        data_acomph: 'latest',
        data_previsao: 'latest',
        versao: 'latest',
        ndiasprev: 15,
      });
    }
  }, [unicenarioScenario, revision, unidade, trigger]);

  const colLabels = rawData?.data?.colLabels ?? [];

  const unicenarioSections: UnicenarioSectionRow[] = useMemo(() => {
    const sections = rawData?.data?.sections ?? [];
    return sections.map((section) => ({
      category: section.category,
      rows: section.rowLabels.map((label, i) => ({
        label,
        values: (section.values[i] || []).map(([value, color]) => {
          const formatted = displayValue(value as number, unidade === '%mlt');
          return {
            value: formatted === NA_LABEL ? EM_DASH : formatted,
            colorName: color,
          };
        }),
      })),
    }));
  }, [rawData, unidade]);

  const hasData = unicenarioSections.some(
    (section) => section.rows.some((row) => row.values.some((v) => v.value !== EM_DASH)),
  );

  return {
    unicenarioSections, colLabels, isFetching, hasData,
  };
}
